import { useRef, useState } from 'react';
import useModal from '@/hooks/useModal';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { INIT_WEB_DOCUMENT_FILTER_STATE } from '@/recoil/init_data';
import {
  StyledCancelButton,
  StyledSubmitButton,
} from '@/components/Approval/CategoryContent/TooltipBox/useDeleteItem/styles';
import { t } from 'i18next';
import { useSnackbar } from '@/hooks/global-tools';
import {
  useDeleteTalkFlowFavoriteFilter,
  useGetMyTalkFlowFavoriteFilter,
} from '@/hooks/apiHooks';
import Spinner from '@/components/@IntranetLibrary/Spinner';

type P = {
  title: string;
  favoriteId: string;
  handleClose?: () => void;
};

const useDeleteItem = ({ title, favoriteId, handleClose }: P) => {
  const deleteModalRef = useRef<ModalRef>(null);
  const [filters, setFilters] = useAtom(documentFilterState);
  const toast = useSnackbar();
  const { mutate } = useGetMyTalkFlowFavoriteFilter();
  const [isLoading, setIsLoading] = useState(false);

  const { openModal, closeModal } = useModal(
    deleteModalRef,
    undefined,
    handleClose,
  );

  const handleDeleteFavoriteItem = async () => {
    setIsLoading(true);
    try {
      await useDeleteTalkFlowFavoriteFilter(favoriteId);
      await mutate();
      setFilters(INIT_WEB_DOCUMENT_FILTER_STATE);
      closeModal();
      toast?.show({
        message: `'${title}' ${t('내 필터가 삭제되었습니다.')}`,
        severity: 'success',
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const DeleteModal = (
    <div onClick={(e) => e.stopPropagation()}>
      <Modal
        ref={deleteModalRef}
        title={t('내 필터 삭제')}
        size={'x-small'}
        handleClose={isLoading ? undefined : closeModal}
        isBlockBackDropClose={isLoading}
        isCloseButtonDisabled={isLoading}
      >
        <div>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '24px',
            }}
          >{`'${title}'\n${t('내 필터를 삭제하시겠어요?')}`}</div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
            <StyledCancelButton
              type="button"
              onClick={closeModal}
              disabled={isLoading}
            >
              {t('취소')}
            </StyledCancelButton>
            <StyledSubmitButton
              onClick={handleDeleteFavoriteItem}
              disabled={isLoading}
            >
              {t('삭제')}
              {isLoading && <Spinner size="10px" theme="white" />}
            </StyledSubmitButton>
          </div>
        </div>
      </Modal>
    </div>
  );
  return { DeleteModal, openModal };
};

export default useDeleteItem;
